import React from 'react';
const INTERVAL_TIME_MS = 50;
const MAX_POLLING_TIME_MS = 5000;
/**
 * A hook that is polling until the element
 * with a given id exists in DOM.
 *
 * Usage
 * ```
 * const isElementInDom = useWaitForDom('[id="DOMElementId"]');
 * ```
 * @returns {null | 'DOMElementId'}
 */
export const useWaitForDom = (selector) => {
    const [elementExists, setElementExists] = React.useState(null);
    /*
    |---------------------------------------------------------------------------------
    | Polling
    |---------------------------------------------------------------------------------
    */
    const pollingIntervalRef = React.useRef(undefined);
    const clearPollingInterval = React.useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }
    }, []);
    const pollForElement = React.useCallback(() => {
        if (selector !== '' && document.querySelector(selector)) {
            setElementExists(selector);
            clearPollingInterval();
        }
    }, [selector, clearPollingInterval]);
    React.useEffect(() => {
        pollForElement();
        if (!elementExists) {
            pollingIntervalRef.current = setInterval(() => {
                pollForElement();
            }, INTERVAL_TIME_MS);
        }
        return clearPollingInterval;
    }, [pollForElement, elementExists, clearPollingInterval]);
    React.useEffect(() => {
        setElementExists(null);
    }, [selector]);
    /*
    |---------------------------------------------------------------------------------
    | Warning if the polling takes too long
    |---------------------------------------------------------------------------------
    */
    const warningTimeoutRef = React.useRef(undefined);
    const clearWarningTimeout = React.useCallback(() => {
        if (warningTimeoutRef.current) {
            clearTimeout(warningTimeoutRef.current);
        }
    }, []);
    React.useEffect(() => {
        if (selector !== '' && !elementExists && !warningTimeoutRef.current) {
            warningTimeoutRef.current = setTimeout(() => {
                if (process.env.NODE_ENV !== 'production') {
                    console.warn(`Element with selector '${selector}' not found after ${MAX_POLLING_TIME_MS} milliseconds. Check if the awaited selector '${selector}' is correct.`);
                }
            }, MAX_POLLING_TIME_MS);
        }
        return clearWarningTimeout;
    }, [selector, elementExists, clearWarningTimeout]);
    React.useEffect(() => {
        if (elementExists) {
            clearWarningTimeout();
        }
    }, [elementExists, clearWarningTimeout]);
    return elementExists;
};
